'use client';

import { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import Link from 'next/link';
import { YouTubePayler } from './YouTubePlayerAdvanced';
import { AnimatedLogoIcon } from '@/_components/AnimatedLogoIcon';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.m4v', '.avi'];

export const getVideoKind = (item) => {
    if (!item) return 'image';
    const src  = (item.itemImageSrc || '').toLowerCase();
    const type = item.type;
    if (type === 'youtube') return 'youtube';
    if (type === 'custom')  return 'custom';
    if (item.videoId)       return 'youtube';
    if (type === 'video') {
        if (VIDEO_EXTS.some(ext => src.includes(ext))) return 'custom';
        if (src.includes('youtube.com') || src.includes('youtu.be')) return 'youtube';
        return 'poster-only';
    }
    return 'image';
};

export const getYouTubeId = (item) => {
    if (!item) return null;
    if (item.videoId) return item.videoId;
    const src   = item.itemImageSrc || '';
    const match = src.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
};

export const getPoster = (item, defaultPoster = null) => {
    if (!item) return defaultPoster;
    if (item.videoCover) return item.videoCover;
    if (item.poster) return item.poster;
    if (item.videoId && item.itemImageSrc) return item.itemImageSrc;
    const kind = getVideoKind(item);
    if (kind === 'youtube') {
        const id = getYouTubeId(item);
        if (id) return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
    }
    return defaultPoster;
};

// ─────────────────────────────────────────────────────────────────────────────
// Play button — animated pulse rings
// ─────────────────────────────────────────────────────────────────────────────

export const PlayBtn = ({ size = 56 }) => (
    <>
        <style>{`
            @keyframes ennix-pulse-a {
                0%   { transform: scale(1);   opacity: 0.55; }
                80%  { transform: scale(1.9); opacity: 0; }
                100% { transform: scale(1.9); opacity: 0; }
            }
            @keyframes ennix-pulse-b {
                0%   { transform: scale(1);   opacity: 0.35; }
                80%  { transform: scale(1.5); opacity: 0; }
                100% { transform: scale(1.5); opacity: 0; }
            }
        `}</style>

        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {/* Outer pulse */}
            <div style={{
                position: 'absolute',
                width: size * 1.75, height: size * 1.75,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.28)',
                animation: 'ennix-pulse-a 2.4s ease-out infinite',
                pointerEvents: 'none',
            }} />
            {/* Inner pulse */}
            <div style={{
                position: 'absolute',
                width: size * 1.38, height: size * 1.38,
                borderRadius: '50%',
                border: '1.5px solid rgba(255,255,255,0.2)',
                animation: 'ennix-pulse-b 2.4s ease-out infinite 0.55s',
                pointerEvents: 'none',
            }} />

            {/* Main circle */}
            <div style={{
                width: size, height: size,
                borderRadius: '50%',
                background: 'linear-gradient(150deg, rgba(255,255,255,0.97) 0%, rgba(240,253,250,0.92) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 2px 8px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.9)',
                flexShrink: 0,
                position: 'relative', zIndex: 1,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            }}>
                <i
                    className="pi pi-play"
                    style={{ color: '#0c1a2e', fontSize: size * 0.36, marginLeft: size * 0.07 }}
                />
            </div>
        </div>
    </>
);

// ─────────────────────────────────────────────────────────────────────────────
// Poster overlay — thumbnail with dark veil + play button
// ─────────────────────────────────────────────────────────────────────────────

const PosterOverlay = ({ poster, alt, isLarge, isCard, fixedHeight, onClick }) => {
    const [hovered, setHovered] = useState(false);
    const h = fixedHeight ? `${fixedHeight}px` : undefined;

    return (
        <div
            className={isLarge ? 'border-1 border-100 p-4 pt-6 border-round-sm' : ''}
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                cursor: 'pointer',
                userSelect: 'none',
                width: '100%',
                height: h,
                overflow: 'hidden',
                borderRadius: fixedHeight ? 8 : undefined,
                background: fixedHeight ? '#0a0f1a' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {poster ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={poster}
                    alt={alt || 'Video preview'}
                    className={isLarge ? 'largeImage border-round-sm' : 'border-round-sm'}
                    loading="lazy"
                    style={{
                        display: 'block',
                        width: '100%',
                        transition: 'transform 0.4s ease',
                        transform: hovered ? 'scale(1.04)' : 'scale(1)',
                        ...(fixedHeight ? { height: `${fixedHeight}px`, objectFit: 'contain' } : {}),
                    }}
                />
            ) : (
                <div style={{
                    background: 'linear-gradient(135deg, #0e1723 0%, #1a2540 100%)',
                    width: '100%',
                    height: h ?? 'auto',
                    aspectRatio: fixedHeight ? undefined : (isCard ? '1 / 1' : '16 / 9'),
                    borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <i className="pi pi-video" style={{ fontSize: '2.5rem', color: '#1bb8a7', opacity: 0.5 }} />
                </div>
            )}

            {/* Cinematic veil + play button */}
            <div style={{
                position: 'absolute', inset: 0,
                borderRadius: 6,
                background: hovered ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.28)',
                transition: 'background 0.25s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <div style={{
                    transform: hovered ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.25s ease',
                }}>
                    <PlayBtn size={isLarge ? 64 : isCard ? 44 : 52} />
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Cinematic Video Dialog
// ─────────────────────────────────────────────────────────────────────────────

export const VideoDialog = ({ visible, onHide, title, slug, children }) => (
    <Dialog
        visible={visible}
        onHide={onHide}
        modal
        dismissableMask
        style={{ width: 'min(94vw, 960px)', border: 'none', overflow: 'hidden', borderRadius: 16, margin: 0 }}
        breakpoints={{ '960px': '96vw', '640px': '100vw' }}
        pt={{
            mask: {
                style: {
                    background: 'rgba(2,6,18,0.9)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                }
            },
            root: {
                style: {
                    background: '#070d1c',
                    border: '1px solid rgba(255,255,255,0.07)',
                    borderTop: '3px solid #1bb8a7',
                    borderRadius: 16,
                    overflow: 'hidden',
                    boxShadow: '0 32px 80px rgba(0,0,0,0.75)',
                    maxHeight: '92dvh',
                    display: 'flex',
                    flexDirection: 'column',
                }
            },
            header: {
                style: {
                    background: '#ffffff',
                    borderBottom: '1px solid #e2e8f0',
                    padding: '9px 14px',
                    minHeight: 'unset',
                    gap: 8,
                    overflow: 'hidden',
                }
            },
            headerTitle: { style: { flex: 1, overflow: 'hidden', minWidth: 0 } },
            content: {
                style: {
                    background: '#070d1c',
                    padding: 0,
                    flex: 1,
                    overflowY: 'auto',
                }
            },
            closeButton: {
                className: 'video-dialog-close-btn',
                style: {
                    borderRadius: '50%',
                    width: 32, height: 32,
                    background: 'var(--primary-color)',
                    border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: 'none',
                }
            },
            closeButtonIcon: { style: { fontSize: '0.72rem', color: '#fff' } },
        }}
        icons={
            slug ? (
                <Link
                    href={`/${slug}`}
                    onClick={onHide}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '5px 11px',
                        background: 'var(--primary-color)',
                        color: '#fff',
                        borderRadius: 7,
                        fontSize: '0.72rem', fontWeight: 600,
                        textDecoration: 'none',
                        flexShrink: 0,
                        marginRight: 6,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <i className="pi pi-external-link" style={{ fontSize: '0.62rem' }} />
                    <span>View Details</span>
                </Link>
            ) : null
        }
        header={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden', flex: 1, minWidth: 0 }}>
                <AnimatedLogoIcon size={32} />
                {title && (
                    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Product
                        </span>
                        <p style={{
                            margin: 0, fontSize: '0.82rem', fontWeight: 700,
                            color: '#1e293b', lineHeight: 1.3,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                            {title}
                        </p>
                    </div>
                )}
            </div>
        }
    >
        <style>{`
            .video-dialog-close-btn,
            .video-dialog-close-btn:hover,
            .video-dialog-close-btn:focus {
                background: var(--primary-color) !important;
                color: #fff !important;
            }
            .video-dialog-close-btn .p-icon,
            .video-dialog-close-btn svg {
                color: #fff !important;
                fill: #fff !important;
            }
        `}</style>
        <div style={{ width: '100%' }}>
            {children}
        </div>
    </Dialog>
);

// ─────────────────────────────────────────────────────────────────────────────
// YouTube Video Slide
// ─────────────────────────────────────────────────────────────────────────────

export const YoutubeVideoSlide = ({ item, type, hideBtn, id, defaultPoster, fixedHeight, slug }) => {
    const [open, setOpen] = useState(false);
    const videoId = getYouTubeId(item);
    const poster  = getPoster(item, defaultPoster);
    const isCard  = !type;
    const isLarge = type === 'large';

    return (
        <div className="product-image">
            <PosterOverlay
                poster={poster}
                alt={item.alt}
                isLarge={isLarge}
                isCard={isCard}
                fixedHeight={fixedHeight}
                onClick={() => setOpen(true)}
            />
            {open && (
                <VideoDialog visible onHide={() => setOpen(false)} title={item.title} slug={slug}>
                    {videoId
                        ? <YouTubePayler videoId={videoId} />
                        : <p style={{ color: '#fb923c', fontSize: '0.85rem' }}>Video ID not available.</p>
                    }
                </VideoDialog>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom (MP4 / S3) Video Slide
// ─────────────────────────────────────────────────────────────────────────────

export const CustomVideoSlide = ({ item, type, defaultPoster, fixedHeight, slug }) => {
    const [open, setOpen] = useState(false);
    const poster   = getPoster(item, defaultPoster);
    const videoUrl = item.itemImageSrc;
    const isCard   = !type;
    const isLarge  = type === 'large';

    return (
        <div className="product-image">
            <PosterOverlay
                poster={poster}
                alt={item.alt}
                isLarge={isLarge}
                isCard={isCard}
                fixedHeight={fixedHeight}
                onClick={() => setOpen(true)}
            />
            {open && (
                <VideoDialog visible onHide={() => setOpen(false)} title={item.title} slug={slug}>
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <video
                        controls
                        autoPlay
                        style={{
                            width: '100%',
                            maxHeight: '62vh',
                            borderRadius: 12,
                            background: '#000',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                            outline: '1px solid rgba(27,184,167,0.2)',
                        }}
                    >
                        <source src={videoUrl} />
                        Your browser does not support the video tag.
                    </video>
                </VideoDialog>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Video Thumbnail (gallery strip)
// ─────────────────────────────────────────────────────────────────────────────

export const VideoThumb = ({ item, defaultPoster }) => {
    const poster = getPoster(item, defaultPoster);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
            {poster ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={poster}
                    alt={item.alt || 'video'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
            ) : (
                <div style={{
                    width: '100%', height: '100%',
                    background: 'linear-gradient(135deg,#0e1723,#1a2540)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <i className="pi pi-video" style={{ color: '#1bb8a7', fontSize: '1.2rem' }} />
                </div>
            )}

            {/* Small video icon — no overlay */}
            <div style={{
                position: 'absolute', bottom: 4, right: 4,
                background: 'rgba(0,0,0,0.35)',
                borderRadius: '50%',
                width: 22, height: 22,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
            }}>
                <i className="pi pi-play-circle" style={{ color: '#fff', fontSize: '0.75rem' }} />
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Universal video renderer
// ─────────────────────────────────────────────────────────────────────────────

export const VideoSlide = ({ item, type, hideBtn, id, defaultPoster, fixedHeight, slug }) => {
    const kind = getVideoKind(item);
    if (kind === 'youtube')
        return <YoutubeVideoSlide item={item} type={type} hideBtn={hideBtn} id={id} defaultPoster={defaultPoster} fixedHeight={fixedHeight} slug={slug} />;
    if (kind === 'custom')
        return <CustomVideoSlide item={item} type={type} defaultPoster={defaultPoster} fixedHeight={fixedHeight} slug={slug} />;
    return (
        <div className="product-image">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={item.itemImageSrc} alt={item.alt || 'Product image'} className="border-round-sm" loading="lazy" style={{ width: '100%' }} />
        </div>
    );
};
